/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import {
    ACTION_VIEW,
    Icon,
    SPACE_FILL,
    STACK_DIRECTION,
    STACK_GAP,
    StackView,
    Text,
    ConfiguredGrid,
    useGlobalLang,
    RequestGridSortDirections,
    useModal,
    LinkButton,
    useGlobalAddons,
} from '@veeam-vspc/shared/components';
import { BackupServerVmwareInventoryType } from '@veeam-vspc/models/rest';
import { deepEqual } from '@veeam-vspc/shared/core';

import type { VirtualServerVirtualMachine, BackupServerBackupJobVmwareObjectSize, VirtualServerTag } from '@veeam-vspc/models/rest';
import type { GridStore, RequestParams } from '@veeam-vspc/shared/components';
import type { FC } from 'react';
import type { RequestSuccessResponse } from '@veeam-vspc/shared/interfaces';
import type { VspcLang } from 'configs/languages';

import { SidePanelForm } from 'components/layouts/SidePanelForm';
import { useJobWizardStore } from '../../../../stores';
import protectedObjectsIconSrc from 'images/actions/protected-objects.png';
import searchIconSrc from 'images/icons/search.svg';
import { ItemsModal } from '../ItemsModal/ItemsModal';
import { BackupOptionsToolbar } from '../BackupOptionsToolbar/BackupOptionsToolbar';
import { BackupOptionsTarget } from '../../../../enums';

interface ConfigureBackupObjectsProps {
    hidePanel: () => void;
}

export const ConfigureBackupObjects: FC<ConfigureBackupObjectsProps> = observer(({ hidePanel }) => {
    const lang = useGlobalLang<VspcLang>();
    const wizardStore = useJobWizardStore();
    const api = useRef<GridStore<BackupServerBackupJobVmwareObjectSize, unknown, unknown>>();
    const { portalEnums } = useGlobalAddons();

    const handleSelectItems = (
        containers: VirtualServerTag[],
        vms: VirtualServerVirtualMachine[],
        deactivate?: () => void,
    ) => {
        wizardStore.handleSelectItems(BackupOptionsTarget.IncludedItems, containers, BackupServerVmwareInventoryType.Tag);
        wizardStore.handleSelectItems(BackupOptionsTarget.IncludedItems, vms, BackupServerVmwareInventoryType.VirtualMachine);

        deactivate && deactivate();

        api.current?.fetchData();
    };

    const handleDeleteItems = (items: BackupServerBackupJobVmwareObjectSize[]) => {
        wizardStore.handleDeleteItems(BackupOptionsTarget.IncludedItems, items);

        api.current?.reloadGrid();
    };

    const fetchData = (
        { page, limit, sort, filter }: RequestParams<BackupServerBackupJobVmwareObjectSize & { name: string; }>,
    ): Promise<RequestSuccessResponse<BackupServerBackupJobVmwareObjectSize[]>> => {
        let items = wizardStore.includedItems.slice();

        if (sort && sort.length) {
            items.sort((a, b) => {
                const { direction, property } = sort[0];

                const aValue = a.inventoryObject[property];
                const bValue = b.inventoryObject[property];

                if (direction === RequestGridSortDirections.Asc) {
                    return aValue
                        .localeCompare(
                            bValue,
                            'kn',
                            { sensitivity: 'base', numeric: true }
                        );
                }

                if (direction === RequestGridSortDirections.Desc) {
                    return bValue
                        .localeCompare(
                            aValue,
                            'kn',
                            { sensitivity: 'base', numeric: true }
                        );
                }

                return 0;
            });
        }

        items = items
            .filter(x => x.inventoryObject.name
                .toLowerCase()
                .includes((filter.name || '').trim().toLowerCase()),
            );

        const chunk = items.slice((page * limit - limit), page * limit);

        return Promise.resolve({
            data: chunk.map(x => ({ ...x, objectId: x.inventoryObject.objectId })),
            meta: {
                pagingInfo: {
                    total: items.length,
                },
            },
        } as RequestSuccessResponse<BackupServerBackupJobVmwareObjectSize[]>);
    };

    const handleApply = () => {
        if (deepEqual(wizardStore.includedItems, wizardStore.data.virtualMachines.includes)) {
            hidePanel();
            return;
        }

        wizardStore.updateSelectedItems();
        wizardStore.updateAppAwareProcessingItems();
        wizardStore.updateGuestFSIndexingItems();

        hidePanel();
    };

    const handleCancel = () => {
        wizardStore.cancelSelectedItems();

        hidePanel();
    };

    const [itemsModal, itemsModalActions] = useModal({
        render: ({ deactivate }) => (
            <ItemsModal
                availablePages={['containers', 'vms']}
                onCancel={deactivate}
                onApply={(containers, vms) => handleSelectItems(containers, vms, deactivate)}
            />
        ),
    });

    return (
        <SidePanelForm
            title={lang.PROTECTED_OBJECTS}
            description={lang.SELECT_OBJECTS_BACK_UP}
            onRequestClose={handleCancel}
            actions={[
                { text: lang.APPLY, onClick: handleApply },
                { text: lang.CANCEL, onClick: handleCancel, view: ACTION_VIEW.secondary },
            ]}
        >
            <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.l} spaceFill={SPACE_FILL.all}>
                <LinkButton
                    iconBefore={searchIconSrc}
                    onClick={() => itemsModalActions.activate()}
                >
                    {`${lang.BROWSE_TO_SELECT_OBJECTS}...`}
                </LinkButton>

                <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.m} spaceFill={SPACE_FILL.all}>
                    <StackView direction={STACK_DIRECTION.row} gap={STACK_GAP.s}>
                        <Icon src={protectedObjectsIconSrc} />
                        <Text>{`${lang.PROTECTED_OBJECTS} (${wizardStore.includedItems.length})`}</Text>
                    </StackView>

                    <ConfiguredGrid
                        api={api}
                        columns={[
                            {
                                id: 'name',
                                title: lang.NAME,
                                cell: rowData => rowData.inventoryObject.name,
                            },
                            {
                                id: 'type',
                                title: lang.TYPE,
                                cell: rowData => portalEnums.getEnumDescription('BackupServerVmwareInventoryType', rowData.inventoryObject.type),
                            },
                        ]}
                        data={fetchData}
                        disableAutoUpdate
                        selection={{
                            field: 'objectId',
                            checkbox: true,
                            multiple: true,
                        }}
                        toolbars={[
                            () => (
                                <BackupOptionsToolbar
                                    onRemove={handleDeleteItems}
                                    viewType={BackupOptionsTarget.IncludedItems}
                                />
                            ),
                        ]}
                    />
                </StackView>
            </StackView>

            {itemsModal}
        </SidePanelForm>
    );
});
